<template>
  <div class="workout">
    <transition name="fade" mode="out-in">
      <template v-if="isVideoWorkout">
        <video-workout
          :workout-id="id"
          :workout-groups="groups"
          :workout-title="title"
          :workout-description="description"
          :video="video"
          :banner="banner"
          @sessionStart="session => (workoutSession = session)"
          @complete="completeWorkout"
        />
      </template>

      <template v-else-if="workoutSession">
        <workout-session
          :session="workoutSession"
          :session-mode="workoutModePreference"
          :workout-id="id"
          :workout-groups="groups"
          :workout-type="workout_type"
          :workout-timer="workoutTimer"
          :audio-sprite-map="audio_sprite_map"
          :hide-timer="hide_timer"
          @complete="completeWorkout"
        />
      </template>

      <template v-else>
        <div class="workout">
          <workout-toolbar :workoutId="id" :page-id="page_id" />

          <template v-if="isPresent(video) || isPresent(banner)">
            <hero :image="banner" :video="video" safe-padding />
          </template>
          <template v-else>
            <div class="v-toolbar__spacer mb-1"></div>
          </template>

          <text-block-header
            :heading="title"
            :content="description"
            :no-collapse="isBeepTest"
            :large="isBeepTest"
          />

          <template v-if="!isBeepTest">
            <workout-group
              v-for="group in groups"
              :key="group.id"
              :group="group"
            />
          </template>

          <cta-row :white-bg="!isBeepTest" row>
            <div
              v-if="!isBeepTest"
              @click="toggleMode"
              class="mr-3 ml-2 text-center"
            >
              <svg width="50" height="32" viewBox="0 0 50 32" xmlns="http://www.w3.org/2000/svg" v-bind:svg-inline="''" :class="[
                  { '--active': isFollowAlongMode },
                  'workout__follow-icon d-block'
                ]" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><g stroke="#000" stroke-width="3" fill="none" fill-rule="evenodd" stroke-linecap="round" stroke-linejoin="round"><path d="M5 13h26.783l-8-8M18.217 19.137H45l-8 8"/></g></svg>
              <span class="text-x-small opacity-60 m-0">Follow</span>
            </div>

            <v-button
              :class="[{ 'mr-0': !isBeepTest }]"
              :is-loading="isLoading"
              @click="startWorkout"
            >
              {{ ctaText }}
            </v-button>
          </cta-row>
        </div>
      </template>
    </transition>

    <alert-dialog
      title="Session Complete!"
      :actions="[
        {
          title: 'Dismiss',
          callback: () => closeCompleteDialog()
        }
      ]"
      :show="showIsCompleteDialog"
      @close="() => closeCompleteDialog()"
      centered
    >
      <v-icon icon="verified_user" size="4rem" color="var(--brand-color-hex)" />
    </alert-dialog>

    <v-made-with-movement v-if="!isDesktop" />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState, mapMutations } from "vuex"

import { WORKOUT_SESSION_MODES, WORKOUT_TYPES } from "@/store/workouts"

import enumValidator from "@/lib/validators/enum-validator"
import EventBus, { EVENTS } from "@/lib/event-bus"
import { isPresent } from "@/lib/utils"
import Timer, { TIMER_EVENTS, initializeAudio } from "@/lib/timer"

import WorkoutToolbar from "@/components/workout/Toolbar"
import AlertDialog from "@/components/AlertDialog"
import Hero from "@/components/Hero"
import TextBlockHeader from "@/components/TextBlockHeader"
import CtaRow from "@/components/CtaRow"

export default {
  components: {
    WorkoutToolbar,
    Hero,
    AlertDialog,
    TextBlockHeader,
    CtaRow,
    VideoWorkout: () => import("@/components/workout/VideoWorkout"),
    WorkoutSession: () => import("@/components/workout/Session"),
    WorkoutGroup: () => import("@/components/workout/Group")
  },

  props: {
    id: { type: Number, required: true },
    page_id: { type: Number, required: true },
    title: { type: String, default: "" },
    description: { type: String, default: "" },
    banner: { type: Object, default: () => ({}) },
    video: { type: Object, default: null },
    groups: { type: Array, default: () => [] },
    workout_type: {
      type: String,
      default: null,
      validator: enumValidator([
        null,
        WORKOUT_TYPES.BEEP_TEST,
        WORKOUT_TYPES.VIDEO_WORKOUT
      ])
    },
    audio_sprite_map: { type: Object, default: null },
    auto_capture: { type: Boolean, default: false },
    auto_capture_form_id: { type: Number, default: null },
    hide_timer: { type: Boolean, default: false }
  },

  head() {
    return {
      title: this.title
    }
  },

  data() {
    return {
      workoutSession: null,
      exercise: null,
      groupTimer: null,
      workoutTimer: null,
      showIsCompleteDialog: false,
      showSignUpPromptDialog: false,
      hasCompleted: false,
      isLoading: false,
      activeGroupIndex: null
    }
  },

  methods: {
    isPresent,

    ...mapState("responsive", ["isDesktop"]),
    ...mapActions("workouts", ["startSession"]),
    ...mapMutations("workouts", ["setWorkoutModePreference"]),
    ...mapMutations("forms", ["setCurrentForm"]),

    startWorkout() {
      this.isLoading = true
      this.workoutTimer = new Timer({ countIn: true })
      this.workoutTimer.on(TIMER_EVENTS.STARTED, () =>
        EventBus.$emit(EVENTS.WORKOUT_STARTED)
      )

      this.startSession({
        workoutId: this.id,
        mode: this.workoutModePreference
      }).then(session => {
        this.workoutSession = session
        this.isLoading = false

        setTimeout(() => {
          this.workoutTimer.start()
        }, 800)
      })
    },

    completeWorkout() {
      if (this.workoutTimer) {
        this.workoutTimer.pause()
        this.workoutTimer.reset()
        this.workoutTimer = null
      }

      if (this.workoutSession) {
        if (this.auto_capture && this.isAuthenticated) {
          if (this.auto_capture_form_id) {
            EventBus.$emit(EVENTS.TRIGGER_FORM, {
              formId: this.auto_capture_form_id
            })
          } else {
            EventBus.$emit(EVENTS.WORKOUT_COMPLETED, { id: this.id })
          }
        } else {
          this.showIsCompleteDialog = true
        }

        this.workoutSession = null
      }
    },

    toggleMode() {
      this.setWorkoutModePreference(
        this.workoutModePreference === WORKOUT_SESSION_MODES.GROUPED
          ? WORKOUT_SESSION_MODES.FOLLOW_ALONG
          : WORKOUT_SESSION_MODES.GROUPED
      )
    },

    closeCompleteDialog() {
      this.showIsCompleteDialog = false

      if (this.isAuthenticated) {
        EventBus.$emit(EVENTS.WORKOUT_COMPLETED, { id: this.id })
      }
    }
  },

  deactivated() {
    this.completeWorkout()
  },

  beforeDestroy() {
    this.completeWorkout()
  },

  mounted() {
    this.isLoading = true

    initializeAudio(this.audio_sprite_map, () => {
      this.isLoading = false
    })
  },

  computed: {
    ...mapGetters("auth", ["isAuthenticated"]),
    ...mapState("workouts", ["workoutModePreference"]),

    isBeepTest() {
      return this.workout_type === WORKOUT_TYPES.BEEP_TEST
    },

    isVideoWorkout() {
      return this.workout_type === WORKOUT_TYPES.VIDEO_WORKOUT
    },

    isFollowAlongMode() {
      return this.workoutModePreference === WORKOUT_SESSION_MODES.FOLLOW_ALONG
    },

    ctaText() {
      return this.isBeepTest ? "Start Beep Test" : "Start Workout"
    }
  }
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";

.workout__follow-icon {
  width: $size--9;
  margin-bottom: -$size--1;

  g {
    stroke: $color-ui--grey-40;
  }

  &.--active {
    g {
      stroke: var(--brand-color-hex);
    }
  }
}
</style>
